import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Iconify from '../iconify';
import { fDateTime } from '../../utils/format-time';
import { useTranslations } from '../../hooks/use-translations';

function siteIdsEqual(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function formatUtcToLocal(dateString) {
  if (!dateString) {
    return '-';
  }

  // Backend stores this field in UTC; if timezone marker is missing,
  // force UTC by appending "Z" before formatting to local browser time.
  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString);
  const normalizedDate = hasTimezone ? dateString : `${dateString}Z`;

  return fDateTime(normalizedDate, 'dd MMM yyyy HH:mm');
}

export default function SiteOpenAlert({ sites, selectedSiteId = null }) {
  const { t } = useTranslations();
  const navigate = useNavigate();

  if (selectedSiteId == null) {
    return null;
  }

  const openSites = (sites || [])
    .filter((s) => s?.doorsHeldOpen)
    .filter((s) => siteIdsEqual(s.id, selectedSiteId));

  if (openSites.length === 0) {
    return null;
  }

  const handleClick = () => {
    navigate('/', { state: { openManageDoors: true } });
  };

  const site = openSites[0];

  return (
    <Tooltip
      arrow
      placement="bottom-end"
      slotProps={{
        tooltip: {
          sx: (theme) => ({
            bgcolor: 'error.dark',
            color: 'common.white',
            border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
            boxShadow: `0 8px 20px ${alpha(theme.palette.error.dark, 0.45)}`,
          }),
        },
        arrow: {
          sx: {
            color: 'error.dark',
          },
        },
      }}
      title={
        <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 600 }}>
          {t('dashboard.siteOpenAlertClickHint')}
        </Typography>
      }
    >
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          mr: 1.5,
          cursor: 'pointer',
          px: 1.2,
          py: 0.55,
          borderRadius: 1,
          border: 'none',
          font: 'inherit',
          fontFamily: 'inherit',
          color: 'error.dark',
          bgcolor: alpha(theme.palette.error.main, 0.18),
          outline: 'none',
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: alpha(theme.palette.error.dark, 0.55),
          boxShadow: `0 0 0 1px ${alpha(theme.palette.error.main, 0.3)}, 0 6px 14px ${alpha(theme.palette.error.main, 0.25)}`,
          maxWidth: { xs: 220, sm: 360 },
          '@keyframes siteOpenAlertPulse': {
            '0%, 100%': {
              boxShadow: `0 0 0 1px ${alpha(theme.palette.error.main, 0.3)}, 0 6px 14px ${alpha(theme.palette.error.main, 0.25)}`,
            },
            '50%': {
              boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.45)}, 0 10px 24px ${alpha(theme.palette.error.main, 0.35)}`,
            },
          },
          animation: 'siteOpenAlertPulse 1.8s ease-in-out infinite',
        })}
      >
        <Iconify icon="ic:sharp-error" width={24} sx={{ flexShrink: 0 }} />
        <Box sx={{ ml: 0.75, minWidth: 0, textAlign: 'left' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>
            {site.name}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.25, opacity: 0.92 }} noWrap>
            {t('dashboard.openSince')} {formatUtcToLocal(site.doorsHeldOpenSinceUtc)}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

SiteOpenAlert.propTypes = {
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      doorsHeldOpen: PropTypes.bool,
      doorsHeldOpenSinceUtc: PropTypes.string,
    })
  ),
  selectedSiteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
